import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router-dom';
import { LayoutContext } from './context/LayoutContext';
import { MenuItems } from "./MenuItems";
import {Button} from "primereact/button";

const GlobalSearchModal = () => {
    const { globalSearchModal, setGlobalSearchModal } = useContext(LayoutContext);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const navigate = useNavigate();

    const inputRef = useRef<HTMLInputElement | null>(null);
    const itemsRef = useRef<Array<HTMLLIElement | null>>([]);

    const allActions = useMemo(() => {
        const flatten = (items: any[], parentPath = ""): any[] => {
            let flatList: any[] = [];
            items.forEach((item) => {
                const currentPath = parentPath ? `${parentPath} > ${item.label}` : item.label;
                if (item.to) {
                    flatList.push({
                        ...item,
                        breadcrumb: currentPath,
                        searchKey: `${item.label} ${currentPath} ${item.description || ''}`.toLowerCase()
                    });
                }
                if (item.items && Array.isArray(item.items)) {
                    flatList = [...flatList, ...flatten(item.items, currentPath)];
                }
            });
            return flatList;
        };
        return flatten(MenuItems);
    }, []);

    // Show all actions if query is empty, otherwise filter
    const filtered = useMemo(() => {
        if (!query.trim()) return allActions;
        return allActions.filter((item) =>
            item.searchKey.includes(query.toLowerCase())
        );
    }, [query, allActions]);

    const onSelect = (item: any) => {
        if (item && item.to) {
            navigate(item.to);
            setGlobalSearchModal(false);
            setQuery('');
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setGlobalSearchModal((prev) => !prev);
                return;
            }

            if (!globalSearchModal) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
            } else if (e.key === 'Enter' && selectedIndex >= 0) {
                e.preventDefault();
                if (filtered[selectedIndex]) {
                    onSelect(filtered[selectedIndex]);
                }
            } else if (e.key === 'Escape') {
                setGlobalSearchModal(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);

    }, [globalSearchModal, filtered, selectedIndex, setGlobalSearchModal]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    useEffect(() => {
        if (globalSearchModal) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [globalSearchModal]);

    useEffect(() => {
        if (selectedIndex >= 0 && itemsRef.current[selectedIndex]) {
            itemsRef.current[selectedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
    }, [selectedIndex]);

    return (
        <Dialog
            visible={globalSearchModal}
            style={{ width: '600px' }}
            modal
            onHide={() => {
                setGlobalSearchModal(false)
                // setQuery('')
            }}
            showHeader={false}
            dismissableMask={true}
            contentClassName="p-0 border-round-xl overflow-hidden shadow-8"
        >
            {/* Header / Search Area */}

            <div className="p-2 border-bottom-1 border-200 bg-white sticky top-0 z-2 flex align-items-center">
                {/* Left side: Search Input */}
                <span className="p-input-icon-left flex-1">
                    <i className="pi pi-search text-xl" />
                    <InputText
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search menu, settings, or management..."
                        className="w-full p-inputtext-lg border-none focus:shadow-none shadow-none bg-transparent"
                    />
                </span>

                {/* Right side: Close Button */}
                <div className="flex align-items-center px-2 border-left-1 border-100">
                    <Button
                        icon="pi pi-times"
                        rounded
                        text
                        severity="secondary"
                        onClick={() => {
                            setGlobalSearchModal(false)
                            setQuery('')
                        }}
                        aria-label="Close"
                        className="h-2rem w-2rem"
                        tooltip="Esc"
                        tooltipOptions={{ position: 'bottom' }}
                    />
                </div>
            </div>

            {/* Main Results Area */}
            <div style={{ height: '450px' }} className="overflow-y-auto bg-white">
                {filtered.length > 0 ? (
                    <ul className="list-none p-0 m-0">
                        {filtered.map((item, idx) => (
                            <li
                                key={item.breadcrumb + idx}
                                ref={(el) => (itemsRef.current[idx] = el)}
                                className={`p-3 cursor-pointer border-bottom-1 border-100 flex align-items-center ${
                                    idx === selectedIndex ? 'bg-primary-50' : 'hover:surface-50'
                                }`}
                                onClick={() => onSelect(item)}
                                onMouseEnter={() => setSelectedIndex(idx)}
                            >
                                <div className={`flex align-items-center justify-content-center border-round w-3rem h-3rem mr-3 ${idx === selectedIndex ? 'bg-primary text-white' : 'bg-gray-100 text-600'}`}>
                                    <i className={`${item.icon || 'pi pi-compass'} text-xl`} />
                                </div>
                                <div className="flex flex-column flex-1">
                                    <div className="flex justify-content-between align-items-center">
                                        <span className="font-bold text-900">{item.label}</span>

                                        {idx === selectedIndex && <i className="pi pi-arrow-right text-primary mr-2" />}
                                    </div>
                                    <small className="text-500 font-medium">{item.breadcrumb}</small>
                                    {item.description && (
                                        <small className="text-400 mt-1">{item.description}</small>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="flex flex-column align-items-center justify-content-center h-full text-500">
                        <i className="pi pi-filter-slash mb-2 text-4xl"></i>
                        <p>No results found for "{query}"</p>
                    </div>
                )}
            </div>

            {/* Footer*/}
            <div className="flex justify-content-between align-items-center px-4 py-3 bg-gray-50 border-top-1 border-200">
                <div className="flex gap-4">
                    <span className="flex align-items-center gap-1 text-500 text-xs">
                        <kbd className="border-1 surface-border border-round px-2 py-1 bg-white shadow-1 font-bold">↑↓</kbd> Navigate
                    </span>
                    <span className="flex align-items-center gap-1 text-500 text-xs">
                        <kbd className="border-1 surface-border border-round px-2 py-1 bg-white shadow-1 font-bold">Enter</kbd> Select
                    </span>
                </div>
                <span className="flex align-items-center gap-1 text-500 text-xs">
                    <kbd className="border-1 surface-border border-round px-2 py-1 bg-white shadow-1 font-bold">Esc</kbd> Close
                </span>
            </div>
        </Dialog>
    );
};

export default GlobalSearchModal;